/**
 * 「我的设备」（ADR-0016）：自己加入过的设备，能改名、能移除；Ta 的设备都丢了时，帮 Ta 生成找回码。
 * 只列自己的（服务端按成员过滤），别人的设备看不到也动不了。顺序和小字在 devices.ts。
 */
import { LIMITS, type DeviceDto } from '@star-sea/shared';
import { cleanLabel, deviceMeta, sortDevices } from './devices.ts';
import { el, input } from './dom.ts';
import { confirmDialog } from './ui.ts';

export interface DevicesHandlers {
  list(): Promise<DeviceDto[]>;
  rename(id: string, label: string): Promise<DeviceDto>;
  remove(id: string): Promise<void>;
  nameOf(memberId: string): string;
  /** 移除的是这台：清掉令牌和离线缓存，回到加入页 */
  removedSelf(): void;
  /** 加自己的另一台设备（打开邀请对话框生成加设备的码） */
  addDevice(): void;
  /** Ta 在星海里时才有：帮 Ta 生成找回码 */
  recoverPartner: (() => void) | null;
  partnerName: string | null;
}

export function devicesDialog(h: DevicesHandlers): void {
  const list = el('div', { class: 'device-list', attrs: { role: 'list' } });
  const err = el('p', { class: 'form-error', attrs: { role: 'alert' } });
  const dialog = el('dialog', { class: 'dialog dialog-devices', attrs: { 'aria-labelledby': 'devices-title' } });
  const close = () => {
    dialog.close();
    dialog.remove();
  };
  dialog.addEventListener('cancel', (e) => {
    e.preventDefault();
    close();
  });
  const fail = (e: unknown) => (err.textContent = e instanceof Error ? e.message : String(e));

  const item = (d: DeviceDto): HTMLElement => {
    const node = el('div', { class: `device-item${d.current ? ' device-current' : ''}${d.recovered ? ' device-recovered' : ''}`, attrs: { role: 'listitem' } });
    const view = () => {
      const rename = el('button', { class: 'btn btn-ghost', text: '改名', attrs: { type: 'button', 'aria-label': `给「${d.label}」改名` } });
      const remove = el('button', { class: 'btn btn-ghost device-remove', text: d.current ? '退出这台' : '移除', attrs: { type: 'button', 'aria-label': `移除「${d.label}」` } });
      rename.addEventListener('click', edit);
      remove.addEventListener('click', async () => {
        const ok = await confirmDialog(
          d.current
            ? { title: '让这台设备退出星海？', text: '这台的令牌和离线缓存会一起清掉。想再回来，要在另一台已经加入的设备上生成邀请码。', confirm: '退出这台', danger: true }
            : { title: `移除「${d.label}」？`, text: '那台设备马上就不能再看、再添照片了，开着的页面也会断开。照片和回忆都还在。', confirm: '移除', danger: true },
        );
        if (!ok) return;
        err.textContent = '';
        rename.disabled = remove.disabled = true;
        try {
          await h.remove(d.id);
          if (d.current) {
            close();
            h.removedSelf();
            return;
          }
          node.remove();
        } catch (e) {
          fail(e);
          rename.disabled = remove.disabled = false;
        }
      });
      node.replaceChildren(
        el('div', { class: 'device-text' }, el('span', { class: 'device-label', text: d.label }), el('span', { class: 'device-meta', text: deviceMeta(d, { now: Date.now(), nameOf: h.nameOf }) })),
        el('div', { class: 'device-actions' }, rename, remove),
      );
    };
    const edit = () => {
      const box = input({ value: d.label, maxlength: String(LIMITS.deviceLabelLength), 'aria-label': '设备名', autocomplete: 'off' });
      const save = el('button', { class: 'btn btn-primary', text: '保存', attrs: { type: 'submit' } });
      const cancel = el('button', { class: 'btn btn-ghost', text: '取消', attrs: { type: 'button' }, on: { click: view } });
      const form = el('form', { class: 'device-edit' }, box, el('div', { class: 'device-actions' }, save, cancel));
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const label = cleanLabel(box.value, LIMITS.deviceLabelLength);
        if (!label) return void (err.textContent = `设备名不能是空的，最多 ${LIMITS.deviceLabelLength} 个字`);
        err.textContent = '';
        save.disabled = true;
        try {
          d = { ...(await h.rename(d.id, label)), current: d.current };
          view();
        } catch (e) {
          fail(e);
          save.disabled = false;
        }
      });
      node.replaceChildren(form);
      box.focus();
      box.select();
    };
    view();
    return node;
  };

  const add = el('button', { class: 'btn', text: '加我的另一台设备', attrs: { type: 'button' } });
  add.addEventListener('click', () => {
    close();
    h.addDevice();
  });
  const recover = h.recoverPartner;
  dialog.append(
    el('h2', { class: 'dialog-title', text: '我的设备', attrs: { id: 'devices-title' } }),
    el('p', { class: 'field-hint', text: '只有你自己的设备。丢了的、不再用的，在这里移除。' }),
    list,
    err,
    el('div', { class: 'dialog-actions' }, add, el('button', { class: 'btn btn-ghost', text: '关闭', attrs: { type: 'button' }, on: { click: close } })),
    ...(recover
      ? [
          el(
            'div',
            { class: 'dialog-section' },
            el('p', { class: 'field-hint', text: `${h.partnerName ?? 'Ta'} 的设备都丢了？生成一个找回码给 Ta，${LIMITS.recoverInviteHours} 小时内有效。用它加入的设备算 Ta 的，Ta 的每台设备都会收到提醒。` }),
            el('button', {
              class: 'btn',
              text: '帮 Ta 找回',
              attrs: { type: 'button' },
              on: {
                click: () => {
                  close();
                  recover();
                },
              },
            }),
          ),
        ]
      : []),
    el('p', { class: 'card-quiet', text: '两个人的设备都丢了：请维护星海的人在服务器上生成找回码。' }),
  );
  list.append(el('p', { class: 'card-quiet', text: '正在读设备…' }));
  void h.list().then(
    (devices) => list.replaceChildren(...sortDevices(devices).map(item)),
    (e: unknown) => {
      list.replaceChildren();
      fail(e);
    },
  );
  document.body.append(dialog);
  dialog.showModal();
}
